import React, { createContext, FC, ReactNode, useEffect, useState } from 'react';

import {
  Connector,
  useAccount,
  useConnect,
  useDisconnect,
  useSwitchNetwork,
  useNetwork,
  usePublicClient,
  useWalletClient,
} from 'wagmi';

import { ConnType, Session } from '~components/utils/types';
import { NetworkIds } from '~config/rpcConfig';

import { AddressString } from './types';

export const SessionContext = createContext<Session | undefined>(undefined);

export const SessionProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { address: userAddress, isConnected, status } = useAccount();
  const { chain: { id: networkId } = { id: NetworkIds.ARBITRUM_ONE } } = useNetwork();
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();
  const connect = useConnect();
  const disconnect = useDisconnect();
  const { switchNetwork } = useSwitchNetwork();

  const [clientAddress, setClientAddress] = useState<AddressString>('0x');
  const [clientIsConnected, setClientIsConnected] = useState(false);
  const [clientStatus, setClientStatus] = useState<ConnType>('disconnected');

  useEffect(() => {
    if (userAddress) {
      setClientAddress(userAddress);
    }
    setClientIsConnected(isConnected);
    setClientStatus(status);
  }, [userAddress, isConnected, status]);

  return (
    <SessionContext.Provider
      value={{
        address: clientAddress,
        network: { networkId, isChainValid: networkId in NetworkIds },
        walletClient: walletClient === null ? undefined : walletClient,
        publicClient,
        connect: (connector: Connector) => connect.connect({ connector }),
        disconnect: () => disconnect.disconnect(),
        switchNet: (newNetwork: NetworkIds) => (switchNetwork ? switchNetwork(newNetwork) : undefined),
        connectionStatus: { status: clientStatus, error: connect.error },
        connectors: connect.connectors,
        isConnected: clientIsConnected,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};
